import { useState, type ReactNode } from 'react';
import type { SpriteSheetCfg } from '../../utils/spriteFrame';
import SpritePortrait from './SpritePortrait';
import styles from './ResultCard.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Quadro do fim de batalha/missão: moldura de metal com rebites, título
// dourado (vitória) ou sangue (derrota), faíscas subindo e — saindo pela
// lateral — o meio corpo do LOCUTOR sorteado (reino na vitória, monstro na
// derrota) com balão de fala. Sem locutor (API fora), cai no busto do sprite.
// Espólios e botões chegam prontos como `children`.
// ─────────────────────────────────────────────────────────────────────────────

export interface ResultSpeaker {
  /** Arte do locutor (URL). Tem prioridade sobre o sprite. */
  image?: string;
  sprite?: string;
  cfg?: SpriteSheetCfg | null;
  name: string;
}

interface Props {
  kind: 'victory' | 'defeat';
  title: string;
  message: string;
  speaker: ResultSpeaker | null;
  quote: string;
  children: ReactNode;
}

/** Brilhos em volta do personagem: [x%, y%, atraso s]. Fixos para não pular a cada render. */
const SPARKLES: [number, number, number][] = [
  [8, 22, 0], [88, 14, 0.7], [4, 62, 1.4], [94, 52, 0.3], [20, 4, 1.1],
  [72, 2, 1.8], [14, 88, 0.5], [86, 84, 1.3], [50, -4, 0.9], [98, 30, 1.6],
];

export default function ResultCard({ kind, title, message, speaker, quote, children }: Props) {
  const [portraitFailed, setPortraitFailed] = useState(false);
  const win = kind === 'victory';
  const hasArt = !!speaker?.image && !portraitFailed;
  const hasPortrait = hasArt || (!!speaker?.sprite && !portraitFailed);
  // Tela estreita: o personagem vai para cima do quadro, menor.
  const [narrow] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(max-width: 760px)').matches);

  return (
    <div className={styles.overlay}>
      <div className={`${styles.stage} ${hasPortrait ? styles.withPortrait : ''}`}>
        <div className={`${styles.card} ${win ? styles.win : styles.lose}`} role="dialog" aria-modal="true" aria-label={title}>
          {/* rebites dos cantos */}
          <span className={`${styles.rivet} ${styles.tl}`} />
          <span className={`${styles.rivet} ${styles.tr}`} />
          <span className={`${styles.rivet} ${styles.bl}`} />
          <span className={`${styles.rivet} ${styles.br}`} />
          {/* faíscas subindo */}
          <div className={styles.embers} aria-hidden="true">
            {Array.from({ length: 14 }, (_, i) => <span key={i} style={{ ['--i' as string]: i }} />)}
          </div>

          <div className={styles.emblem} aria-hidden="true">{win ? '🏆' : '💀'}</div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.message}>{message}</p>
          <div className={styles.body}>{children}</div>
        </div>

        {speaker && (
          <div className={`${styles.speaker} ${win ? styles.hero : styles.foe}`}>
            <div className={styles.figure}>
              {/* redemoinho mágico + brilhos atrás do personagem */}
              {hasPortrait && (
                <div className={styles.aura} aria-hidden="true">
                  {SPARKLES.map(([x, y, d], i) => (
                    <span key={i} style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${d}s` }} />
                  ))}
                </div>
              )}
              {hasArt ? (
                <img src={speaker.image} alt="" className={styles.art} style={{ height: narrow ? 210 : 360 }}
                  onError={() => setPortraitFailed(true)} />
              ) : hasPortrait && (
                <SpritePortrait sprite={speaker.sprite!} cfg={speaker.cfg} height={narrow ? 170 : 300}
                  className={styles.portrait} onFail={() => setPortraitFailed(true)} />
              )}
            </div>
            <div className={styles.bubble}>
              <span className={styles.bubbleName}>{speaker.name}</span>
              {quote}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
